/****
 * Accessible session timeout warning dialog that alerts users before
 * automatic sign-out, provides countdown visibility, supports keyboard
 * navigation, and integrates with text-to-speech accessibility features.
 */
import type { FC, MouseEvent } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAccessibilitySettings } from "@/hooks/useAccessibilitySettings";
import { TTSRuntime } from "@/lib/accessibility";

interface SessionTimeoutWarningProps {
  open: boolean;
  warningSeconds: number;
  onStaySignedIn: () => void;
  onLogout: () => void;
}

const formatSeconds = (seconds: number) => {
  if (seconds <= 0) return "00:00";

  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;

  return `${minutes.toString().padStart(2, "0")}:${remaining.toString().padStart(2, "0")}`;
};

const SessionTimeoutWarning: FC<SessionTimeoutWarningProps> = ({
  open,
  warningSeconds,
  onStaySignedIn,
  onLogout,
}) => {
  const { t } = useTranslation();
  const { settings } = useAccessibilitySettings();

  const modalRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef<HTMLDivElement>(null);

  // Manage focus trapping, accessibility announcements, and TTS playback
  // while the session warning dialog is visible
  useEffect(() => {
    if (!open) return;

    const node = modalRef.current;

    const focusable = node?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const first = focusable?.[0];
    const last = focusable?.[focusable.length - 1];

    first?.focus();

    if (liveRef.current) {
      liveRef.current.textContent = "";
      setTimeout(() => {
        liveRef.current!.textContent =
          t("components.sessionTimeout.title") +
          ". " +
          t("components.sessionTimeout.countdown", {
            time: formatSeconds(warningSeconds),
          });
      }, 100);
    }

    if (settings.ttsEnabled) {
      TTSRuntime.stop();
      TTSRuntime.setRate(settings.speechRate);

      TTSRuntime.speak(
        t("components.sessionTimeout.title") +
          ". " +
          t("components.sessionTimeout.description") +
          ". " +
          t("components.sessionTimeout.countdown", {
            time: formatSeconds(warningSeconds),
          })
      );
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab" && focusable && focusable.length > 0) {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    };

    node?.addEventListener("keydown", handleKeyDown);

    return () => {
      node?.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, t, settings.ttsEnabled, settings.speechRate]);

  if (!open) return null;

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 px-4 py-6">
      <div
        ref={liveRef}
        aria-live="assertive"
        aria-atomic="true"
        className="fixed left-[-9999px] w-px h-px overflow-hidden"
      />

      <div
        ref={modalRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-desc session-timeout-countdown"
        className="max-w-[420px] w-full rounded-3xl border border-[var(--border-color)] bg-white p-6 shadow-2xl"
        onClick={handleBackdropClick}
      >
        <div
          id="session-timeout-title"
          className="mb-4 text-sm font-semibold uppercase tracking-[0.12em] text-slate-500"
        >
          {t("components.sessionTimeout.title")}
        </div>

        <div className="space-y-4 text-slate-800">
          <p id="session-timeout-desc" className="text-base font-medium">
            {t("components.sessionTimeout.description")}
          </p>

          <div
            id="session-timeout-countdown"
            aria-label={t("components.sessionTimeout.countdown", {
              time: formatSeconds(warningSeconds),
            })}
            className="rounded-2xl bg-slate-100 p-4 text-center text-lg font-semibold text-slate-900"
          >
            {t("components.sessionTimeout.countdown", {
              time: formatSeconds(warningSeconds),
            })}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-100"
            >
              {t("components.sessionTimeout.logout")}
            </button>

            <button
              type="button"
              onClick={onStaySignedIn}
              className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              {t("components.sessionTimeout.stay")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
